import { Typography, Box, Tabs, Tab, SxProps } from '@mui/material';

const tabsListSx: SxProps = {
  borderLeft: '1px solid',
  borderColor: 'primary.main',
  minWidth: '300px',
  '& .MuiTabs-indicator': {
    backgroundColor: 'primary.main',
    width: '6px',
    left: 0
  },
  '& .MuiTab-root.Mui-selected': {
    backgroundColor: 'white'
  }
};

const tabSx: SxProps = {
  alignItems: 'flex-start',
  textAlign: 'left',
  p: '10px 20px 20px !important',
  minHeight: 'auto',
  borderBottom: 'none',
  borderLeft: '4px solid transparent',
  '&:hover': {
    backgroundColor: '#E4E1FF'
  }
};

const tabLabelSx: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  width: '100%',
  textTransform: 'none',
  fontSize: '16px',
  fontWeight: 600,
  color: 'text.primary'
};

export type VerticalTabItem = {
  id: string;
  title: string;
  subtitle?: string | null;
  description?: string | null;
};

type VerticalTabsProps = {
  items?: VerticalTabItem[];
  emptyMessage?: string;
  defaultSelectedIndex?: number;
  containerSx?: SxProps;
  showUnderline?: boolean;
  onTabChange?: (item: VerticalTabItem, index: number) => void;
  onTabClick?: (item: VerticalTabItem, index: number) => void;
};

export const VerticalTabs: React.FC<VerticalTabsProps> = ({
  items = [],
  emptyMessage = 'No items available',
  defaultSelectedIndex = 0,
  containerSx,
  showUnderline = true,
  onTabChange,
  onTabClick
}) => {
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    onTabChange?.(items[newValue], newValue);
  };

  if (items.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">{emptyMessage}</Typography>
      </Box>
    );
  }

  // Ensure the value is valid
  const validIndex =
    defaultSelectedIndex >= 0 && defaultSelectedIndex < items.length
      ? defaultSelectedIndex
      : 0;

  return (
    <Tabs
      orientation="vertical"
      value={validIndex}
      onChange={handleTabChange}
      sx={{ ...tabsListSx, ...containerSx }}
      variant="scrollable"
      scrollButtons={false}
    >
      {items.map((item, index) => (
        <Tab
          key={item.id}
          onClick={() => onTabClick?.(item, index)}
          label={
            <Box sx={tabLabelSx}>
              <Typography
                variant="subTitle"
                sx={{
                  color:
                    validIndex === index ? 'customGrey.dark' : 'primary.main',
                  textDecoration:
                    validIndex === index || !showUnderline
                      ? 'none'
                      : 'underline'
                }}
              >
                {item.title}
              </Typography>
              <Typography variant="body2" color="customGrey.dark" mt={1}>
                {item.subtitle}
                {item.subtitle && item.description && ' | '}
                {item.description}
              </Typography>
            </Box>
          }
          sx={tabSx}
        />
      ))}
    </Tabs>
  );
};
